import { SearchMode, useSearch } from "~/core/composables/useSearch";
import LxcError from "~/core/models/LxcError.class";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import {
  Filters,
  type FiltersSelection,
  FiltersType,
  OPERATOR_RSQL_ILIKE,
} from "~/core/models/filters";
import filtersUtils from "~/core/utils/filters.utils";
import type { UserProfile } from "~/modules/user/models/UserProfile.interface";
import userProfileBusinessUnitService from "~/modules/user/services/userProfileBusinessUnit.service";
import userProfileOrganizationService from "~/modules/user/services/userProfileOrganization.service";
import userProfileSuperAdministrationService from "~/modules/user/services/userProfileSuperAdministration.service";

export function useFetchProfiles({
  businessUnitCode,
  organizationCode,
  searchMode = SearchMode.URL_SEARCH,
}: UseFetchParameters) {
  let service = userProfileSuperAdministrationService.getUserProfiles.bind(
    userProfileSuperAdministrationService,
  );

  if (organizationCode) {
    service = userProfileOrganizationService.getUserProfiles.bind(
      userProfileOrganizationService,
    );
  } else if (businessUnitCode) {
    service = userProfileBusinessUnitService.getUserProfiles.bind(
      userProfileBusinessUnitService,
    );
  }

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.LABEL,
      {
        key: ["label"],
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );

  const results: Ref<UserProfile[] | undefined> = ref();
  const isLoading: Ref<boolean> = ref(false);
  const error: Ref<LxcError | undefined> = ref();

  async function fetchData() {
    isLoading.value = true;
    const response = await service({
      businessUnitCode,
      organizationCode,
      searchParams: searchParams.value,
    });
    if (LxcError.check(response)) {
      error.value = response;
    } else {
      results.value = response;
    }
    isLoading.value = false;
  }

  return {
    isLoading,
    error,
    results,
    filters,
    fetchData,
    setFilter,
    search,
    onSearch,
  };
}
